'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import { AlertCircle, CheckCircle, Info, X, XCircle } from 'lucide-react'
import * as React from 'react'

import { cn } from '../lib/utils'

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive:
          "text-destructive bg-card [&>svg]:text-current data-[slot=alert-description]:*:text-destructive/90",
        success:
          'bg-card text-card-foreground [&>svg]:text-green-600 dark:[&>svg]:text-green-400 data-[slot=alert-description]:*:text-muted-foreground',
        warning:
          'bg-card text-card-foreground [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400 data-[slot=alert-description]:*:text-muted-foreground',
        error:
          'text-destructive bg-card [&>svg]:text-current data-[slot=alert-description]:*:text-destructive/90',
        info: 'bg-card text-card-foreground [&>svg]:text-blue-600 dark:[&>svg]:text-blue-400 data-[slot=alert-description]:*:text-muted-foreground'
      },
      size: {
        sm: 'px-3 py-2 text-xs [&>svg]:size-3',
        default: 'px-4 py-3 text-sm [&>svg]:size-4',
        lg: 'px-6 py-4 text-base [&>svg]:size-5'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

const icons = {
  success: CheckCircle,
  warning: AlertCircle,
  error: XCircle,
  info: Info,
  destructive: XCircle
}

interface AlertProps 
  extends React.HTMLAttributes<HTMLDivElement>, 
          VariantProps<typeof alertVariants> {
  title?: string
  description: string | React.ReactNode
  alertIcon?: boolean
  closeButton?: boolean
  autoFade?: boolean
  centered?: boolean
  icon?: React.ElementType
  autoFadeTimeout?: number
  onClose?: () => void
}

export function Alert({
  className,
  variant = 'info',
  size = 'default',
  title,
  description,
  alertIcon = true,
  closeButton = false,
  autoFade = false,
  centered = false,
  icon,
  autoFadeTimeout = 5000,
  onClose,
  ...props
}: AlertProps) {
  const [isVisible, setIsVisible] = React.useState(true)
  const [isAnimating, setIsAnimating] = React.useState(false)
  const Icon = icon || (variant && icons[variant as keyof typeof icons])

  const handleClose = React.useCallback(() => {
    setIsAnimating(true)
    setTimeout(() => {
      setIsVisible(false)
      onClose?.()
    }, 200)
  }, [onClose])

  React.useEffect(() => {
    if (autoFade && autoFadeTimeout > 0) {
      const timer = setTimeout(handleClose, autoFadeTimeout)
      return () => clearTimeout(timer)
    }
  }, [autoFade, autoFadeTimeout, handleClose])

  if (!isVisible || !description) return null

  const alertContent = (
    <div 
      data-slot="alert"
      role="alert"
      className={cn(
        alertVariants({ variant, size }), 
        'transform transition-all duration-200 ease-in-out',
        isAnimating && 'scale-95 opacity-0',
        'max-w-full overflow-hidden',
        closeButton && 'pr-12', // Add extra padding for close button
        className
      )} 
      {...props}
    >
      {alertIcon && Icon && (
        <Icon className="transition-colors duration-200" />
      )}
      
      <div className="col-start-2 grid justify-items-start gap-1">
        {title && (
          <div
            data-slot="alert-title"
            className="line-clamp-1 min-h-4 font-medium tracking-tight"
          >
            {title}
          </div>
        )}
        {description && (
          <div
            data-slot="alert-description"
            className="text-muted-foreground grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed [&_a]:underline [&_a]:underline-offset-2 [&_a]:decoration-current/50 [&_a]:hover:decoration-current"
          >
            {description}
          </div>
        )}
      </div>
      
      {closeButton && (
        <button
          onClick={handleClose}
          className="absolute right-2 top-2 p-1.5 rounded-lg opacity-60 hover:opacity-100 hover:bg-current/10 transition-all duration-200 focus:outline-hidden focus:ring-2 focus:ring-current/20 focus:ring-offset-2 focus:ring-offset-transparent"
          aria-label="Close alert"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )

  return centered ? (
    <div className="flex justify-center w-full px-4 sm:px-6">
      <div className="w-full max-w-2xl">
        {alertContent}
      </div>
    </div>
  ) : (
    alertContent
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-muted-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed",
        className
      )}
      {...props}
    />
  )
}

export { AlertDescription, AlertTitle }

